import React, {forwardRef} from 'react';
import {Input} from './input';
import {cn} from '@/lib/utils';

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void;
}

/**
 * Formats a phone number as the user types
 * Supports US/Canada numbers with +1 prefix: +1 (XXX) XXX-XXXX
 * Also handles international numbers with basic formatting
 */
export function formatPhoneNumber(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // If empty, return empty
  if (!digits) return '';

  // If user is deleting and we're at just "+1", allow clearing
  if (value === '+1' || value === '+') return '';

  // If doesn't start with 1, assume US number and prepend 1
  let workingDigits = digits;
  if (!digits.startsWith('1') && digits.length <= 10) {
    workingDigits = '1' + digits;
  }

  // Handle international numbers (more than 11 digits or doesn't start with 1 but has more than 10 digits)
  if (workingDigits.length > 11 || (digits.length > 10 && !digits.startsWith('1'))) {
    // For international numbers, just add + prefix and basic spacing
    if (workingDigits.length <= 3) {
      return '+' + workingDigits;
    } else {
      return '+' + workingDigits.slice(0, 15); // Limit to reasonable length
    }
  }

  // Format US/Canada numbers: +1 (XXX) XXX-XXXX
  if (workingDigits.length <= 1) {
    return '+1';
  } else if (workingDigits.length <= 4) {
    return `+1 (${workingDigits.slice(1)}`;
  } else if (workingDigits.length <= 7) {
    return `+1 (${workingDigits.slice(1, 4)}) ${workingDigits.slice(4)}`;
  } else {
    return `+1 (${workingDigits.slice(1, 4)}) ${workingDigits.slice(4, 7)}-${workingDigits.slice(7, 11)}`;
  }
}

/**
 * Extracts the raw phone number in E.164 format (+1XXXXXXXXXX)
 */
export function getE164PhoneNumber(formattedValue: string): string {
  const digits = formattedValue.replace(/\D/g, '');
  if (!digits) return '';

  // If doesn't start with 1, assume US number and prepend 1
  if (!digits.startsWith('1') && digits.length <= 10) {
    return '+1' + digits;
  }

  return '+' + digits;
}

/**
 * Validates if a phone number is in valid E.164 format
 */
export function isValidPhoneNumber(phoneNumber: string): boolean {
  return /^\+[1-9]\d{1,14}$/.test(phoneNumber);
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, onChange, onValueChange, onFocus, value, ...props }, ref) => {
    // Format the value prop so stored E.164 numbers display nicely
    const formattedValue = value ? formatPhoneNumber(String(value)) : value;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhoneNumber(e.target.value);
      e.target.value = formatted;

      if (onChange) {
        onChange(formatted);
      }
      if (onValueChange) {
        onValueChange(formatted);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      // Position cursor after +1 if field only contains +1
      if (e.target.value === '+1') {
        setTimeout(() => {
          e.target.setSelectionRange(2, 2);
        }, 0);
      }

      if (onFocus) {
        onFocus(e);
      }
    };

    return (
      <Input
        type="tel"
        autoComplete="tel"
        placeholder="+1 (555) 123-4567"
        className={cn(className)}
        onChange={handleChange}
        onFocus={handleFocus}
        value={formattedValue}
        ref={ref}
        {...props}
      />
    );
  },
);

PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
